import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchPrimaryProcurementCategoriesForTenant,
  dealMatchesPrimaryProcurementCategories,
} from '@/lib/procurementTenantLiveDeals';
import { encodeCategoriesQueryParam } from '@/utils/edtechCategoryNames';
import { normalizeDeal } from '@/utils/procurementDealUtils';
import { fetchFitBulkLatest } from '@/lib/api/fit';
import {
  fetchMyTendersConfig,
  fetchMyTendersAugment,
  setDealStatus,
  clearDealStatus,
  resolveDealId,
} from '@/lib/api/myTenders';
import { buildDealTasks, daysUntilDeadline, isGoodFitDeal } from '@/lib/homeDealTasks';
import { markOnce } from './pilotPerf';

const API_BASE_URL = 'https://api.neogtm.com';
const FIT_CHUNK = 100;
const AUGMENT_CHUNK = 200;
const CHUNK_CONCURRENCY = 4;
const LIVE_MAX_PAGES = 10;
const CLOSED_MAX_PAGES = 20;
const CATEGORY_QUERY_TIMEOUT_MS = 15000;

async function fetchPages(tenantId, query, maxPages, timeoutMs = 0) {
  const all = [];
  let cursor = null;
  let page = 0;
  do {
    page += 1;
    if (page > maxPages) break;
    const cursorParam = cursor ? `&cursor=${encodeURIComponent(JSON.stringify(cursor))}` : '';
    const ctl = timeoutMs ? new AbortController() : null;
    const timer = ctl ? setTimeout(() => ctl.abort(), timeoutMs) : null;
    let res;
    try {
      res = await fetch(
        `${API_BASE_URL}/api/procurement/query?table=live&limit=500&skipFit=true${query}${cursorParam}`,
        { headers: { 'x-tenant-id': tenantId }, credentials: 'include', signal: ctl?.signal }
      );
    } finally {
      if (timer) clearTimeout(timer);
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (page === 1) throw new Error(data?.error || `Live deals failed (${res.status})`);
      break;
    }
    all.push(...(data.data || []));
    cursor = data.nextCursor;
  } while (cursor);
  return all;
}

/**
 * Same request as Market / Procurement Live Radar (table=live + the tenant's primary categories),
 * so Home shows the same deals those pages do.
 *
 * 🔴 [2026-11-04] The category-filtered query can hang. For polleverywhere,
 * categories=Classroom Engagement & Polling,… took 131–207 s server-side (Cloud Run log), and
 * while it hangs it holds one of the browser's connections to the API, so every other call on the
 * page queues behind it (product-profiles: 60 ms on the server, 10–18 s in the browser). So it gets
 * a timeout, and on timeout the UNFILTERED slice is used with the same category match the Market
 * uses. The unfiltered slice is NOT a substitute in general: it leaves out deals with no deadline
 * (feedbackfruits_V2: 80 deals filtered on the server, 30 from the unfiltered slice), which is why
 * it is only the fallback.
 */
async function fetchLiveSlice(tenantId, categories, view, maxPages) {
  const viewParam = view ? `&view=${encodeURIComponent(view)}` : '';
  if (!categories?.length) return fetchPages(tenantId, viewParam, maxPages);
  try {
    return await fetchPages(
      tenantId,
      `${viewParam}&categories=${encodeCategoriesQueryParam(categories)}`,
      maxPages,
      CATEGORY_QUERY_TIMEOUT_MS
    );
  } catch (e) {
    if (e?.name !== 'AbortError') throw e;
    console.warn(`[PilotHome] ${view || 'live'} category query timed out after ${CATEGORY_QUERY_TIMEOUT_MS} ms; using the unfiltered slice`);
    const rows = await fetchPages(tenantId, viewParam, maxPages);
    return rows.filter((d) => dealMatchesPrimaryProcurementCategories(d, categories));
  }
}

/** Run `fn` over `size`-item chunks of `items`, `concurrency` at a time; results merged. Measured on
 * feedbackfruits_V2 (769 recently-closed deals): fit 2.7 s one chunk at a time, 0.9 s four at a time. */
async function mapChunks(items, size, concurrency, fn) {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) chunks.push(items.slice(i, i + size));
  const merged = {};
  for (let i = 0; i < chunks.length; i += concurrency) {
    const parts = await Promise.all(chunks.slice(i, i + concurrency).map((c) => fn(c).catch((e) => {
      console.warn('[PilotHome] chunk failed:', e?.message || e);
      return {};
    })));
    for (const part of parts) Object.assign(merged, part || {});
  }
  return merged;
}

async function hydrateFit(tenantId, deals) {
  const ids = [...new Set(deals.map((d) => d.fit_deal_id).filter(Boolean))];
  const fitMap = await mapChunks(ids, FIT_CHUNK, CHUNK_CONCURRENCY, (c) => fetchFitBulkLatest(tenantId, c));
  return deals.map((d) => {
    const fit = fitMap[d.fit_deal_id] || {};
    return { ...d, fit_light_label: fit.light?.label ?? d.fit_light_label ?? null };
  });
}

function fetchStatuses(tenantId, deals) {
  const ids = [...new Set(deals.map(resolveDealId).filter(Boolean))];
  return mapChunks(ids, AUGMENT_CHUNK, CHUNK_CONCURRENCY, (c) => fetchMyTendersAugment(tenantId, c));
}

/* Last result per tenant, so a return visit paints at once and refreshes behind it. Only the fields
 * the tiles read are kept, and only the closed deals that can show (good fit) or raise a task
 * (a status). localStorage can be absent or full: every access is guarded and a miss is a no-op. */
const CACHE_PREFIX = 'pilot-home-deals-v2:';
const CACHE_TTL_MS = 30 * 60 * 1000;
const KEEP = ['id', 'deal_id', 'fit_deal_id', 'title', 'institution', 'account_name', 'account_id', 'deadline', 'status', 'category', 'fit_light_label', 'source_url'];
const compact = (d) => Object.fromEntries(KEEP.map((k) => [k, d[k] ?? null]));

function readCache(tenantId) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + tenantId);
    if (!raw) return null;
    const c = JSON.parse(raw);
    return c && Date.now() - c.at < CACHE_TTL_MS ? c : null;
  } catch {
    return null;
  }
}

function writeCache(tenantId, { live, closed, augment, enabled }) {
  try {
    const keepClosed = closed.filter((d) => isGoodFitDeal(d) || augment[resolveDealId(d)]?.status);
    const ids = new Set([...live, ...keepClosed].map(resolveDealId));
    const statuses = {};
    for (const id of ids) if (augment[id]?.status) statuses[id] = { status: augment[id].status };
    localStorage.setItem(CACHE_PREFIX + tenantId, JSON.stringify({
      at: Date.now(), enabled, live: live.map(compact), closed: keepClosed.map(compact), augment: statuses,
    }));
  } catch {
    /* storage unavailable or full */
  }
}

/** normalizeDeal drops account_id; Tasks needs it to find the buyer's logo. */
const toDeal = (d) => ({ ...normalizeDeal(d), account_id: d.account_id ?? null });

function byDeadlineAsc(a, b) {
  const da = daysUntilDeadline(a);
  const db = daysUntilDeadline(b);
  if (da == null && db == null) return 0;
  if (da == null) return 1;
  if (db == null) return -1;
  return da - db;
}

/**
 * Live + recently-closed deals in the tenant's primary categories, with fit light and My Deals
 * status. One loader for the Live Deals tile and the deal rules in Tasks, so a status set in one
 * shows in the other without a refetch.
 */
export default function usePilotDeals(tenantId) {
  const [state, setState] = useState({ loading: true, closedLoading: true, error: null, live: [], closed: [], enabled: false });
  const [augment, setAugment] = useState({});
  const [savingId, setSavingId] = useState(null);
  const loadSeq = useRef(0);
  const touched = useRef(new Set());
  const pickOptimistic = (m) => Object.fromEntries([...touched.current].filter((id) => id in m).map((id) => [id, m[id]]));

  useEffect(() => {
    if (!tenantId) return undefined;
    const seq = ++loadSeq.current;
    const current = () => seq === loadSeq.current;
    const cached = readCache(tenantId);
    if (cached) {
      setAugment(cached.augment || {});
      setState({ loading: false, closedLoading: false, error: null, live: cached.live || [], closed: cached.closed || [], enabled: !!cached.enabled, refreshing: true });
      markOnce('deals_cache_painted');
    } else {
      setState({ loading: true, closedLoading: true, error: null, live: [], closed: [], enabled: false });
      setAugment({});
    }
    (async () => {
      let enabled = false;
      let closedP = null;
      let liveFit = [];
      let liveRecords = {};
      try {
        const [categories, config] = await Promise.all([
          fetchPrimaryProcurementCategoriesForTenant(tenantId),
          fetchMyTendersConfig(tenantId).catch(() => ({ enabled: false })),
        ]);
        enabled = !!config?.enabled;
        markOnce('deals_categories');
        // Both slices start together; the closed one is only awaited after the live tab is painted.
        closedP = fetchLiveSlice(tenantId, categories, 'recently_closed', CLOSED_MAX_PAGES)
          .then((rows) => rows.map(toDeal));
        closedP.catch(() => {});
        const live = (await fetchLiveSlice(tenantId, categories, null, LIVE_MAX_PAGES)).map(toDeal);
        markOnce('deals_live_rows');
        [liveFit, liveRecords] = await Promise.all([
          hydrateFit(tenantId, live),
          enabled ? fetchStatuses(tenantId, live) : {},
        ]);
        if (!current()) return;
        // A refresh replaces the cached view; statuses the rep set meanwhile (optimistic) win.
        // Fresh statuses replace cached ones; a status the rep set during this visit wins over both.
        setAugment((m) => ({ ...m, ...liveRecords, ...pickOptimistic(m) }));
        setState((s) => ({ ...s, loading: false, error: null, live: liveFit, enabled }));
        markOnce('deals_live_ready');
      } catch (e) {
        if (!current()) return;
        if (cached) {
          console.warn('[PilotHome] refresh failed, keeping the cached view:', e?.message || e);
          setState((s) => ({ ...s, refreshing: false }));
        } else {
          setState({ loading: false, closedLoading: false, error: e?.message || 'Could not load deals', live: [], closed: [], enabled: false });
        }
        return;
      }
      try {
        const closed = await closedP;
        markOnce('deals_closed_rows');
        const [records, closedFit] = await Promise.all([
          enabled ? fetchStatuses(tenantId, closed) : {},
          hydrateFit(tenantId, closed),
        ]);
        if (!current()) return;
        setAugment((m) => ({ ...m, ...records, ...pickOptimistic(m) }));
        setState((s) => ({ ...s, closedLoading: false, closed: closedFit, refreshing: false }));
        markOnce('deals_closed_ready');
      } catch (e) {
        console.warn('[PilotHome] recently closed deals failed:', e?.message || e);
        if (current()) setState((s) => ({ ...s, closedLoading: false, refreshing: false }));
      }
    })();
    return undefined;
  }, [tenantId]);

  // Keep the cache in step with what is on screen once a full load has landed (statuses included).
  useEffect(() => {
    if (!tenantId || state.loading || state.closedLoading || state.refreshing || state.error) return;
    writeCache(tenantId, { live: state.live, closed: state.closed, augment, enabled: state.enabled });
  }, [tenantId, state.loading, state.closedLoading, state.refreshing, state.error, state.live, state.closed, state.enabled, augment]);

  const changeStatus = useCallback(async (dealId, nextStatus) => {
    if (!tenantId || !dealId) return;
    const previous = augment[dealId] || {};
    const clearing = !nextStatus || nextStatus === 'untouched';
    setSavingId(dealId);
    touched.current.add(dealId);
    setAugment((m) => ({ ...m, [dealId]: { ...previous, status: clearing ? null : nextStatus } }));
    try {
      if (clearing) await clearDealStatus(tenantId, dealId);
      else await setDealStatus(tenantId, dealId, nextStatus);
    } catch (e) {
      setAugment((m) => ({ ...m, [dealId]: previous }));
      throw e;
    } finally {
      setSavingId(null);
    }
  }, [tenantId, augment]);

  const goodFitLive = useMemo(
    () => state.live.filter(isGoodFitDeal).filter((d) => {
      const days = daysUntilDeadline(d);
      return days == null || days >= 0;
    }).sort(byDeadlineAsc),
    [state.live]
  );

  /* Procurement's My Deals is every live + recently-closed deal in the tenant's primary categories
   * (MyTendersTab / myTendersTotalCount) — status is a column there, not the filter. Home keeps the
   * Strong / Moderate Fit ones. Order: open status with the deadline passed (needs an outcome),
   * then live by deadline, then recently closed. */
  const myDeals = useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const d of [...state.live, ...state.closed]) {
      const id = resolveDealId(d);
      if (!id || seen.has(id) || !isGoodFitDeal(d)) continue;
      seen.add(id);
      rows.push(d);
    }
    const open = new Set(['reviewing', 'pursuing', 'submitted']);
    const rank = (d) => {
      const days = daysUntilDeadline(d);
      if (days != null && days < 0) return open.has(augment[resolveDealId(d)]?.status) ? 0 : 2;
      return 1;
    };
    return rows.sort((a, b) => (rank(a) - rank(b)) || byDeadlineAsc(a, b));
  }, [state.live, state.closed, augment]);

  const dealTasks = useMemo(
    () => buildDealTasks([...state.live, ...state.closed], augment),
    [state.live, state.closed, augment]
  );

  return { ...state, augment, goodFitLive, myDeals, dealTasks, changeStatus, savingId };
}
